import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["id", "description", "footer", "thumbnailSrc", "thumbnailOverlay", "disabled", "name", "onClick", "to", "use", "href", "title", "externalLinkProps"];
let _ = t => t,
  _t,
  _t2,
  _t3,
  _t4,
  _t5;
import { useState } from 'react';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { css } from 'academy-common-lib/utils/Styling';
import ACSkeletonBox from '../loading/ACSkeletonBox';
import I18n from 'I18n';
import ACDynamicThumbnailImage from './ACDynamicThumbnailImage';
import { Text } from 'foundations-components/components/Text';
import { HR } from 'foundations-components/transitional/components/HR';
import styledWithTheme from 'foundations-theming/utils';
import { View } from 'foundations-components/transitional/components/View';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Image } from 'foundations-components/transitional/components/Image';
import { Flex } from 'foundations-components/components/Flex';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const RectangularThumbnailContainer = styledWithTheme.div(_t || (_t = _`
  position: relative;
  border-radius: ${0};
  aspect-ratio: 16 / 9;
  overflow: hidden;
`), ({
  theme: trellisTheme
}) => trellisTheme.borderRadius['100']);
const linkStyles = css(_t2 || (_t2 = _`
  flex-direction: column;
  height: 100%;
  color: inherit;
  font: inherit;
  line-height: inherit;
  text-decoration: inherit;
  display: flex;
  -webkit-font-smoothing: auto;
  :hover {
    color: inherit;
    font: inherit;
    line-height: inherit;
    text-decoration: inherit;
  }
`));
const FlexLink = styled(Link).withConfig({
  displayName: "ACContentCard__FlexLink",
  componentId: "sc-1eadu5c-0"
})(["", ""], linkStyles);
const ExternalLink = styled.a.withConfig({
  displayName: "ACContentCard__ExternalLink",
  componentId: "sc-1eadu5c-1"
})(["", ""], linkStyles);
const ContentCardLink = ({
  children,
  externalLinkProps,
  to,
  href,
  onClick = () => {}
}) => {
  if (!to && !href) {
    return /*#__PURE__*/_jsx(Flex, {
      blockSize: "100%",
      flexDirection: "column",
      onClick: onClick,
      children: children
    });
  }
  if (to) {
    return /*#__PURE__*/_jsx(FlexLink, {
      onClick: onClick,
      to: to,
      children: children
    });
  }
  return /*#__PURE__*/_jsx(ExternalLink, Object.assign({}, externalLinkProps, {
    href: href,
    onClick: onClick,
    children: children
  }));
};
const CardWrapper = styledWithTheme.div(_t3 || (_t3 = _`
  width: 100%;
  border-radius: ${0};
  overflow: hidden;
  padding: ${0};
  transition: background-color 200ms;

  ${0}
`), ({
  theme: trellisTheme
}) => trellisTheme.borderRadius['200'], ({
  theme: trellisTheme
}) => trellisTheme.space['200'], ({
  $use,
  theme: trellisTheme
}) => $use !== 'no-hover' && css(_t4 || (_t4 = _`
      &:hover {
        background-color: ${0};
      }
    `), $use === 'on-light' ? trellisTheme.color['fill-tertiary-hover'] : $use === 'on-surface-recessed' ? trellisTheme.color['fill-surface-recessed-hover'] : 'transparent'));
const OpaqueOverlay = styledWithTheme.div(_t5 || (_t5 = _`
  position: absolute;
  height: 100%;
  width: 100%;
  background-color: ${0};
  opacity: 0.6;
  z-index: 1;
  pointer-events: none;
  top:0;
`), ({
  theme: trellisTheme
}) => trellisTheme.color['fill-primary-disabled']);
const ACContentCard = _ref => {
  let {
      id,
      description,
      footer,
      thumbnailSrc,
      thumbnailOverlay,
      disabled = false,
      name,
      onClick = () => {},
      to,
      use = 'on-light',
      href,
      title = name,
      externalLinkProps
    } = _ref,
    props = _objectWithoutPropertiesLoose(_ref, _excluded);
  const [thumbnailLoaded, setThumbnailLoaded] = useState(false);
  const thumbnailLoading = thumbnailSrc && !thumbnailLoaded;
  const textColor = disabled ? 'text-core-disabled' : use === 'on-dark' ? 'text-core-on-fill-default' : 'text-core-default';
  return (
    /*#__PURE__*/
    // @bend-expect trellis-tokenized
    _jsx(CardWrapper, Object.assign({
      $use: use,
      "aria-disabled": disabled,
      "data-test-id": "track-card",
      "data-test-is-ghost": disabled
    }, props, {
      children: /*#__PURE__*/_jsxs(ContentCardLink, {
        externalLinkProps: externalLinkProps,
        href: href,
        onClick: onClick,
        to: to,
        children: [/*#__PURE__*/_jsxs(RectangularThumbnailContainer, {
          children: [/*#__PURE__*/_jsx(ACSkeletonBox, {
            style: {
              position: 'absolute',
              height: '100%',
              width: '100%'
            }
          }), thumbnailSrc ? /*#__PURE__*/_jsx(Image, {
            alt: I18n.text('academyCommon.contentCard.thumbnailAlt', {
              title: name
            }),
            inlineSize: "100%",
            loading: "lazy",
            onLoad: () => setThumbnailLoaded(true),
            position: "absolute",
            src: thumbnailSrc,
            visibility: thumbnailLoading ? 'hidden' : 'visible'
          }) : /*#__PURE__*/_jsx(ACDynamicThumbnailImage, {
            seed: id,
            style: {
              position: 'absolute',
              width: '100%'
            },
            title: name,
            type: "rectangle"
          }), /*#__PURE__*/_jsxs(View, {
            blockSize: "100%",
            inlineSize: "100%",
            position: "absolute",
            children: [thumbnailOverlay, disabled && /*#__PURE__*/_jsx(OpaqueOverlay, {})]
          })]
        }), /*#__PURE__*/_jsxs(TFlex, {
          flex: "2",
          flexDirection: "column",
          justifyContent: "space-between",
          paddingBlockStart: "500",
          children: [/*#__PURE__*/_jsxs(View, {
            inlineSize: "100%",
            children: [/*#__PURE__*/_jsx(Text, {
              as: "h4",
              color: textColor,
              children: title
            }), description && /*#__PURE__*/_jsx(HR, {}), description && /*#__PURE__*/_jsx(View, {
              children: /*#__PURE__*/_jsx(Text, {
                color: textColor,
                children: typeof description === 'string' && description.length > 150 ? `${description.slice(0, 150)}...` : description
              })
            })]
          }), /*#__PURE__*/_jsx(View, {
            inlineSize: "inherit",
            paddingBlockStart: "300",
            children: footer
          })]
        })]
      })
    }))
  );
};
export default ACContentCard;